import React, { useState, useEffect, useMemo } from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { AnchorModel } from "common247";
import { useFirebase } from "@/components/firebase/useFirebase";
import { getMyAnchors } from "@/services/firebase/anchor-functions";
import { ThemedText } from "@/components/ThemedText";
import { ThemedInput } from "@/components/ThemedInput";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedPopupScreen } from "@/components/ThemedPopupScreen";
import { useComponentTheme } from "@/hooks/useComponentTheme";
import type { IPopoverAnchor } from "@/utils/popover-positioning";

/**
 * Props for the AnchorSelector popup component.
 */
export interface IAnchorSelectorProps {
  /** Whether the anchor selector popup is visible */
  visible: boolean;
  /** Callback fired when the selector is dismissed */
  onClose: () => void;
  /** Optional contextual coordinates of cursor or trigger element */
  anchorPosition?: IPopoverAnchor | null;
  /** Single anchor selection callback */
  onSelectAnchor?: (anchor: AnchorModel) => void;
  /** Multi-anchor selection callback */
  onSelectMultipleAnchors?: (anchors: AnchorModel[]) => void;
  /** Whether multi-select is enabled (default: false) */
  multiSelect?: boolean;
  /** Pre-selected anchor IDs */
  selectedAnchorIds?: string[];
  /** Optional pre-fetched anchors */
  anchors?: AnchorModel[];
  /** Optional title override (default: "Select Anchor") */
  title?: string;
  /** Optional subtitle or instructions */
  subtitle?: string;
  /** Target width for the popover card (default: 440) */
  width?: number;
  /** Target maximum height for the popover card (default: 520) */
  maxHeight?: number;
  /** Optional testID for testing */
  testID?: string;
}

const DEFAULT_EMPTY_IDS: string[] = [];

/**
 * Contextual AnchorSelector powered by ThemedPopupScreen.
 * Provides searchable anchor selection anchored near the user cursor/trigger.
 */
export function AnchorSelector({
  visible,
  onClose,
  anchorPosition,
  onSelectAnchor,
  onSelectMultipleAnchors,
  multiSelect = false,
  selectedAnchorIds = DEFAULT_EMPTY_IDS,
  anchors: propAnchors,
  title = "Select Anchor",
  subtitle,
  width = 440,
  maxHeight = 500,
  testID = "anchor-selector",
}: IAnchorSelectorProps): React.JSX.Element {
  const services = useFirebase();
  const [internalAnchors, setInternalAnchors] = useState<AnchorModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>(selectedAnchorIds);

  const { selector: selectorTheme, palette } = useComponentTheme();
  const cardBg = palette.card;
  const borderColor = selectorTheme.row.restingBorderColor;
  const hoverBorderColor = selectorTheme.row.hoverBorderColor;
  const tintColor = selectorTheme.row.selectedBorderColor;
  const textMuted = selectorTheme.row.subtitleColor;
  const badgeBg = selectorTheme.avatarBadge.backgroundColor;

  useEffect(() => {
    setSelectedIds((prev) => {
      if (
        prev.length === selectedAnchorIds.length &&
        prev.every((id, idx) => id === selectedAnchorIds[idx])
      ) {
        return prev;
      }
      return selectedAnchorIds;
    });
  }, [selectedAnchorIds, visible]);

  useEffect(() => {
    if (!visible) return;
    if (propAnchors && propAnchors.length > 0) {
      setInternalAnchors(propAnchors);
      return;
    }
    let isMounted = true;
    const fetchAnchors = async () => {
      setLoading(true);
      try {
        const res = await getMyAnchors(services, {});
        if (isMounted && res.status && res.anchors) {
          setInternalAnchors(res.anchors);
        }
      } catch (err) {
        console.error("[AnchorSelector] Failed to load anchors:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchAnchors();
    return () => {
      isMounted = false;
    };
  }, [visible, propAnchors, services]);

  const filteredAnchors = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return internalAnchors;
    return internalAnchors.filter((a) => {
      const name = (a.name || "").toLowerCase();
      const desc = (a.description || "").toLowerCase();
      const addr = (a.address || "").toLowerCase();
      const type = (a.type || "").toLowerCase();
      return name.includes(q) || desc.includes(q) || addr.includes(q) || type.includes(q);
    });
  }, [internalAnchors, searchQuery]);

  const handleToggleAnchor = (anchor: AnchorModel) => {
    const id = anchor.id;
    if (!id) return;

    if (!multiSelect) {
      onSelectAnchor?.(anchor);
      onClose();
      return;
    }

    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleConfirmMultiSelect = () => {
    if (!multiSelect) return;
    const selectedAnchors = internalAnchors.filter((a) => selectedIds.includes(a.id));
    onSelectMultipleAnchors?.(selectedAnchors);
    onClose();
  };

  return (
    <ThemedPopupScreen
      visible={visible}
      onClose={onClose}
      anchor={anchorPosition || undefined}
      width={width}
      maxHeight={maxHeight}
      title={title}
      subtitle={subtitle}
      testID={testID}
      footerActions={
        multiSelect ? (
          <View style={styles.footerWrap}>
            <ThemedButton
              title="Cancel"
              variant="minimal"
              onPress={onClose}
              style={styles.footerBtn}
            />
            <ThemedButton
              title={`Confirm (${selectedIds.length})`}
              onPress={handleConfirmMultiSelect}
              style={styles.footerBtn}
            />
          </View>
        ) : undefined
      }
    >
      <View style={styles.container}>
        {/* Search input */}
        <View style={styles.searchRow}>
          <ThemedInput
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Search anchors by name, type, address..."
            style={styles.searchInput}
            testID={`${testID}-search-input`}
          />
        </View>

        {loading ? (
          <View style={styles.centered}>
            <ActivityIndicator size="small" color={tintColor} />
            <ThemedText style={[styles.loadingText, { color: textMuted }]}>
              Loading anchors...
            </ThemedText>
          </View>
        ) : filteredAnchors.length === 0 ? (
          <View style={styles.centered}>
            <Ionicons name="boat-outline" size={32} color={textMuted} />
            <ThemedText style={[styles.emptyText, { color: textMuted }]}>
              {searchQuery ? "No matching anchors found" : "No anchors available"}
            </ThemedText>
          </View>
        ) : (
          <ScrollView
            style={styles.listScroll}
            contentContainerStyle={styles.listContent}
            keyboardShouldPersistTaps="handled"
          >
            {filteredAnchors.map((anchor) => {
              const isSelected = selectedIds.includes(anchor.id);
              return (
                <TouchableOpacity
                  key={anchor.id}
                  style={[
                    styles.anchorRow,
                    {
                      backgroundColor: isSelected ? `${tintColor}15` : cardBg,
                      borderColor: isSelected ? tintColor : borderColor,
                    },
                  ]}
                  onPress={() => handleToggleAnchor(anchor)}
                  activeOpacity={0.7}
                  testID={`${testID}-item-${anchor.id}`}
                >
                  <View style={[styles.iconBox, { backgroundColor: badgeBg }]}>
                    <Ionicons name="boat-outline" size={18} color={tintColor} />
                  </View>
                  <View style={styles.anchorInfo}>
                    <ThemedText style={styles.anchorName} numberOfLines={1}>
                      {anchor.name}
                    </ThemedText>
                    {Boolean(anchor.type || anchor.address) && (
                      <ThemedText
                        style={[styles.anchorMeta, { color: textMuted }]}
                        numberOfLines={1}
                      >
                        {[anchor.type ? anchor.type.toUpperCase() : "", anchor.address]
                          .filter(Boolean)
                          .join(" • ")}
                      </ThemedText>
                    )}
                  </View>
                  {multiSelect && (
                    <Ionicons
                      name={isSelected ? "checkbox" : "square-outline"}
                      size={20}
                      color={isSelected ? tintColor : textMuted}
                    />
                  )}
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        )}
      </View>
    </ThemedPopupScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 8,
  },
  searchRow: {
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  searchInput: {
    height: 38,
  },
  centered: {
    padding: 32,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  loadingText: {
    fontSize: 13,
  },
  emptyText: {
    fontSize: 13,
  },
  listScroll: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 16,
    paddingBottom: 16,
    gap: 6,
  },
  anchorRow: {
    flexDirection: "row",
    alignItems: "center",
    padding: 10,
    borderRadius: 8,
    borderWidth: 1,
    gap: 12,
  },
  iconBox: {
    width: 32,
    height: 32,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  anchorInfo: {
    flex: 1,
  },
  anchorName: {
    fontSize: 14,
    fontWeight: "600",
  },
  anchorMeta: {
    fontSize: 12,
    marginTop: 2,
  },
  footerWrap: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  footerBtn: {
    minWidth: 80,
  },
});
